// src/pages/Dashboard/Admin/Votes/components/VoteResultsDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import {
  FaChartBar,
  FaChartPie,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";

import { getVoteResults } from "../../../../../services/voteServices";

import {
  getPercent,
  getQuestionTypeLabel,
  stripHtml,
  sumChoiceCounts,
} from "../utils/votesDashboardUtils";

const CHART_COLORS = [
  "#8ab4f8",
  "#f6c26b",
  "#81c995",
  "#f28b82",
  "#c58af9",
  "#80d8d1",
  "#ffb3c1",
  "#aed581",
  "#ffd8a8",
  "#b39ddb",
  "#90caf9",
  "#ffe082",
];

function ellipsize(value = "", max = 34) {
  const text = stripHtml(value);

  if (text.length <= max) return text;

  return `${text.slice(0, max - 1)}…`;
}

function CustomTooltip({ active, payload, label, total }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const value = Number(item.value || 0);
  const percent = getPercent(value, total);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">
        {item.name || label}
      </p>

      <p className="mt-1 text-slate-600 dark:text-slate-300">
        {value} réponse(s) · {percent}%
      </p>
    </div>
  );
}

function ResultSummaryCard({ label, value, detail }) {
  return (
    <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
        {value}
      </p>

      {detail ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {detail}
        </p>
      ) : null}
    </div>
  );
}

export default function VoteResultsDrawer({ isOpen, vote, onClose }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOpen, setExpandedOpen] = useState({});

  const voteId = vote?.id;

  useEffect(() => {
    if (!isOpen || !voteId) return;

    let mounted = true;

    async function loadResults() {
      try {
        setLoading(true);
        setExpandedOpen({});

        const data = await getVoteResults(voteId);

        if (!mounted) return;

        setResults(Array.isArray(data?.results) ? data.results : []);
      } catch (error) {
        console.error("Erreur chargement résultats :", error);
        toast.error("Erreur lors du chargement des résultats.");
        setResults([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadResults();

    return () => {
      mounted = false;
    };
  }, [isOpen, voteId]);

  const summary = useMemo(() => {
    const questionsCount = results.length;

    let choicesCount = 0;
    let openResponsesCount = 0;
    let totalCountedResponses = 0;

    results.forEach((question) => {
      choicesCount += Array.isArray(question?.choices)
        ? question.choices.length
        : 0;

      openResponsesCount += Array.isArray(question?.responses)
        ? question.responses.length
        : 0;

      totalCountedResponses += sumChoiceCounts(question?.choices || []);
    });

    return {
      questionsCount,
      choicesCount,
      openResponsesCount,
      totalCountedResponses,
    };
  }, [results]);

  if (!isOpen || !vote) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Résultats
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaChartBar className="text-indigo-600 dark:text-indigo-300" />
              {vote.title || "Vote sans titre"}
            </h2>

            <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Consultez les résultats question par question, avec graphiques
              pour les réponses fermées et affichage détaillé des réponses
              libres.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-32 animate-pulse rounded-[24px] bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : !results.length ? (
            <div className="rounded-[28px] border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-950">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                <FaChartPie />
              </div>

              <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
                Aucun résultat disponible
              </h3>

              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Les réponses apparaîtront ici dès que les adhérents auront
                participé.
              </p>
            </div>
          ) : (
            <>
              <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <ResultSummaryCard
                  label="Questions"
                  value={summary.questionsCount}
                  detail="Questions avec résultats"
                />

                <ResultSummaryCard
                  label="Choix"
                  value={summary.choicesCount}
                  detail="Choix proposés"
                />

                <ResultSummaryCard
                  label="Réponses comptées"
                  value={summary.totalCountedResponses}
                  detail="Votes ou sélections"
                />

                <ResultSummaryCard
                  label="Réponses libres"
                  value={summary.openResponsesCount}
                  detail="Commentaires saisis"
                />
              </section>

              <div className="space-y-5">
                {results.map((question, index) => {
                  const choices = (question?.choices || [])
                    .map((choice) => ({
                      ...choice,
                      count: Number(choice?.count || 0),
                    }))
                    .sort((a, b) => b.count - a.count);

                  const total = sumChoiceCounts(choices);
                  const isSingle = question?.type === "single";
                  const isMultiple = question?.type === "multiple";
                  const isOpen = question?.type === "open";

                  const rawResponses = Array.isArray(question?.responses)
                    ? question.responses
                    : [];

                  const openTexts = rawResponses
                    .map((response) =>
                      typeof response === "string"
                        ? response
                        : response?.response_text || ""
                    )
                    .filter(Boolean);

                  const expanded = !!expandedOpen[question.question_id];
                  const visibleOpenTexts = expanded
                    ? openTexts
                    : openTexts.slice(0, 6);

                  return (
                    <article
                      key={question.question_id || index}
                      className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950"
                    >
                      <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-sm font-black text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                                {index + 1}
                              </span>

                              <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                                {getQuestionTypeLabel(question.type)}
                              </span>

                              <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                                {isOpen
                                  ? `${openTexts.length} réponse(s)`
                                  : `${total} réponse(s) comptée(s)`}
                              </span>
                            </div>

                            <div
                              className="prose prose-sm max-w-none text-slate-800 dark:prose-invert dark:text-slate-100"
                              dangerouslySetInnerHTML={{
                                __html: question.question_text || "",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-4 sm:p-5">
                        {(isSingle || isMultiple) && choices.length ? (
                          isSingle ? (
                            <div className="grid gap-4 xl:grid-cols-[420px,1fr]">
                              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                                <div className="mb-3 flex items-center gap-2">
                                  <FaChartPie className="text-indigo-600 dark:text-indigo-300" />
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                                    Répartition
                                  </h4>
                                </div>

                                <div className="h-[300px] w-full">
                                  <ResponsiveContainer>
                                    <PieChart>
                                      <Pie
                                        data={choices}
                                        dataKey="count"
                                        nameKey="choice_text"
                                        innerRadius="55%"
                                        outerRadius="82%"
                                        paddingAngle={2}
                                      >
                                        {choices.map((_, choiceIndex) => (
                                          <Cell
                                            key={choiceIndex}
                                            fill={
                                              CHART_COLORS[
                                                choiceIndex %
                                                  CHART_COLORS.length
                                              ]
                                            }
                                          />
                                        ))}
                                        <LabelList
                                          position="outside"
                                          formatter={(value) =>
                                            `${getPercent(value, total)}%`
                                          }
                                        />
                                      </Pie>

                                      <Tooltip
                                        content={({ active, payload }) => (
                                          <CustomTooltip
                                            active={active}
                                            payload={payload}
                                            total={total}
                                          />
                                        )}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>
                              </div>

                              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                                    Détail des choix
                                  </h4>

                                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                    Total : {total}
                                  </span>
                                </div>

                                <div className="space-y-3">
                                  {choices.map((choice, choiceIndex) => {
                                    const percent = getPercent(
                                      choice.count,
                                      total
                                    );

                                    return (
                                      <div
                                        key={choice.id || choiceIndex}
                                        className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
                                      >
                                        <div className="mb-2 flex items-center justify-between gap-3">
                                          <div className="flex min-w-0 items-center gap-2">
                                            <span
                                              className="h-3 w-3 shrink-0 rounded"
                                              style={{
                                                backgroundColor:
                                                  CHART_COLORS[
                                                    choiceIndex %
                                                      CHART_COLORS.length
                                                  ],
                                              }}
                                            />

                                            <p className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                                              {choice.choice_text}
                                            </p>
                                          </div>

                                          <p className="shrink-0 text-sm font-black text-slate-900 dark:text-white">
                                            {choice.count} · {percent}%
                                          </p>
                                        </div>

                                        <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                          <div
                                            className="h-full rounded-full bg-indigo-500"
                                            style={{ width: `${percent}%` }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                              <div className="mb-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <FaChartBar className="text-violet-600 dark:text-violet-300" />
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                                    Sélections multiples
                                  </h4>
                                </div>

                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                  Total sélections : {total}
                                </span>
                              </div>

                              <div
                                className="w-full"
                                style={{
                                  height: Math.max(260, choices.length * 44),
                                }}
                              >
                                <ResponsiveContainer>
                                  <BarChart
                                    layout="vertical"
                                    data={choices.map((choice) => ({
                                      ...choice,
                                      label: ellipsize(choice.choice_text, 42),
                                    }))}
                                    margin={{
                                      left: 20,
                                      right: 35,
                                      top: 10,
                                      bottom: 10,
                                    }}
                                  >
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis
                                      type="category"
                                      dataKey="label"
                                      width={170}
                                    />

                                    <Tooltip
                                      content={({ active, payload, label }) => (
                                        <CustomTooltip
                                          active={active}
                                          payload={payload}
                                          label={label}
                                          total={total}
                                        />
                                      )}
                                    />

                                    <Bar
                                      dataKey="count"
                                      radius={[6, 6, 6, 6]}
                                    >
                                      {choices.map((_, choiceIndex) => (
                                        <Cell
                                          key={choiceIndex}
                                          fill={
                                            CHART_COLORS[
                                              choiceIndex %
                                                CHART_COLORS.length
                                            ]
                                          }
                                        />
                                      ))}

                                      <LabelList
                                        dataKey="count"
                                        position="right"
                                      />
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          )
                        ) : null}

                        {(isSingle || isMultiple) && !choices.length ? (
                          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                            Aucun choix ou aucun résultat disponible pour cette
                            question.
                          </div>
                        ) : null}

                        {isOpen ? (
                          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                                Réponses libres
                              </h4>

                              <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                                {openTexts.length} réponse(s)
                              </span>
                            </div>

                            {openTexts.length ? (
                              <>
                                <div className="space-y-2">
                                  {visibleOpenTexts.map((text, responseIndex) => (
                                    <div
                                      key={`${question.question_id}-${responseIndex}`}
                                      className="rounded-2xl bg-white p-3 text-sm leading-6 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-800"
                                    >
                                      {text}
                                    </div>
                                  ))}
                                </div>

                                {openTexts.length > 6 ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedOpen((prev) => ({
                                        ...prev,
                                        [question.question_id]:
                                          !prev[question.question_id],
                                      }))
                                    }
                                    className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                                  >
                                    {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                    {expanded ? "Voir moins" : "Voir toutes les réponses"}
                                  </button>
                                ) : null}
                              </>
                            ) : (
                              <p className="text-sm italic text-slate-500 dark:text-slate-400">
                                Aucune réponse libre.
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}